/* eslint-disable no-magic-numbers -- screen-space hit testing and NDC conversion */
import { Plane, Vector3 } from '@technology/web3d-plugin-threejs';
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import { screenPositionToRay } from '@technology/web3d/dist/esnext/Picker/Picker.js';

const dragPlane = new Plane();
const dragPlaneHit = new Vector3();
const dragLineProjected = new Vector3();

export interface TextDragConstraintSettings {
  readonly constrainDragZ?: boolean;
  readonly constrainDragToZAxis?: boolean;
}

export type DragConstraintAnchor = {
  lockedX: number;
  lockedY: number;
  lockedZ: number;
};

export function isDragZConstrained(
  settings: TextDragConstraintSettings
): boolean {
  return settings.constrainDragZ === true;
}

export function isDragZAxisConstrained(
  settings: TextDragConstraintSettings
): boolean {
  return settings.constrainDragToZAxis === true;
}

export function isDragConstrained(
  settings: TextDragConstraintSettings
): boolean {
  return isDragZConstrained(settings) || isDragZAxisConstrained(settings);
}

export function intersectDragPlaneAtZ(
  api: Api,
  screenX: number,
  screenY: number,
  lockedZ: number,
  target: Vector3 = dragPlaneHit
): Vector3 | null {
  const container = api.container ?? api.renderingManager.renderer?.domElement;
  if (!container) {
    return null;
  }

  dragPlane.set(new Vector3(0, 0, 1), -lockedZ);
  const ray = screenPositionToRay(
    { x: screenX, y: screenY },
    container,
    api.camera
  );
  return ray.intersectPlane(dragPlane, target) ? target : null;
}

function intersectDragLineAtXYFromScreen(
  api: Api,
  screenY: number,
  lockedX: number,
  lockedY: number,
  lockedZ: number,
  container: HTMLElement,
  target: Vector3
): Vector3 {
  const rect = container.getBoundingClientRect();
  dragLineProjected.set(lockedX, lockedY, lockedZ).project(api.camera);
  const anchorScreenY =
    rect.top + (-dragLineProjected.y * 0.5 + 0.5) * rect.height;

  dragLineProjected.set(lockedX, lockedY, lockedZ + 1).project(api.camera);
  const plusOneScreenY =
    rect.top + (-dragLineProjected.y * 0.5 + 0.5) * rect.height;
  const pixelsPerUnit = Math.abs(plusOneScreenY - anchorScreenY);

  const zDelta =
    pixelsPerUnit > 1e-6 ? (anchorScreenY - screenY) / pixelsPerUnit : 0;
  target.set(lockedX, lockedY, lockedZ + zDelta);
  return target;
}

export function intersectDragLineAtXY(
  api: Api,
  screenX: number,
  screenY: number,
  lockedX: number,
  lockedY: number,
  lockedZ: number,
  target: Vector3 = dragPlaneHit
): Vector3 | null {
  const container = api.container ?? api.renderingManager.renderer?.domElement;
  if (!container) {
    return null;
  }

  const ray = screenPositionToRay(
    { x: screenX, y: screenY },
    container,
    api.camera
  );
  const lineOrigin = new Vector3(lockedX, lockedY, lockedZ);
  const lineDirection = new Vector3(0, 0, 1);
  const rayDirection = ray.direction;
  const w = ray.origin.clone().sub(lineOrigin);

  const a = rayDirection.dot(rayDirection);
  const b = rayDirection.dot(lineDirection);
  const c = lineDirection.dot(lineDirection);
  const d = rayDirection.dot(w);
  const e = lineDirection.dot(w);
  const denom = a * c - b * b;

  if (Math.abs(denom) >= 1e-10) {
    const lineParam = (a * e - b * d) / denom;
    target.set(lockedX, lockedY, lockedZ + lineParam);
    return target;
  }

  dragPlane.set(new Vector3(0, 1, 0), -lockedY);
  if (ray.intersectPlane(dragPlane, target)) {
    target.x = lockedX;
    target.y = lockedY;
    return target;
  }

  dragPlane.set(new Vector3(1, 0, 0), -lockedX);
  if (ray.intersectPlane(dragPlane, target)) {
    target.x = lockedX;
    target.y = lockedY;
    return target;
  }

  return intersectDragLineAtXYFromScreen(
    api,
    screenY,
    lockedX,
    lockedY,
    lockedZ,
    container,
    target
  );
}

export function resolveConstrainedDragPoint(
  api: Api,
  screenX: number,
  screenY: number,
  anchor: DragConstraintAnchor,
  settings: TextDragConstraintSettings,
  target: Vector3 = dragPlaneHit
): Vector3 | null {
  if (isDragZAxisConstrained(settings)) {
    return intersectDragLineAtXY(
      api,
      screenX,
      screenY,
      anchor.lockedX,
      anchor.lockedY,
      anchor.lockedZ,
      target
    );
  }

  if (isDragZConstrained(settings)) {
    return intersectDragPlaneAtZ(api, screenX, screenY, anchor.lockedZ, target);
  }

  return null;
}
